//----- Component --------------------------------------------------------/
// Developer : Daiana Curcio/Nicolas Pascucci
// Creation Date : 13/03/2024
// Version : 2
// Description : Dialog para finalizar un viaje con fecha de llegada, punto de venta y remito
//------Changes ----------------------------------------------------------//
// Change Date : 04/11/2026
// Change by :
// Change description: Rediseño, fecha de llegada, marca de recibido, validación de errores
//========================================================================/
import React, { useRef, useState } from "react";
import { UrlApi } from "../../services/apirest";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import moment from "moment";
import { logEventos } from "../../services/services";

function DatoViaje({ titulo, valor }) {
  return (
    <Grid item xs={6}>
      <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
        {titulo}
      </Typography>
      <Typography sx={{ fontSize: 13, fontWeight: 500 }}>
        {valor || "-"}
      </Typography>
    </Grid>
  );
}

function Dialog_recibido(props) {
  const { abrirDialogRecibido, row, onClose } = props;

  const hoy = moment().format("YYYY-MM-DD");
  // La salida solo limita la fecha si ya ocurrió; un viaje programado a futuro no la usa.
  const fechaMinima = row?.fecha && row.fecha <= hoy ? row.fecha : undefined;

  const [fechaLlegada, setFechaLlegada] = useState(
    row?.fecha_llegada || hoy
  );
  const [puntoVenta, setPuntoVenta] = useState(
    row?.nro_remito_puntodeventa || ""
  );
  const [numeroRemito, setNumeroRemito] = useState(
    row?.nro_remito_numero || ""
  );
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");
  const guardandoRef = useRef(false);

  const cerrar = () => {
    if (guardandoRef.current) return;
    if (onClose) onClose();
  };

  const validarFecha = () => {
    if (!fechaLlegada) return "Indicá la fecha de llegada.";
    if (fechaLlegada > hoy) return "La fecha de llegada no puede ser futura.";
    if (fechaMinima && fechaLlegada < fechaMinima) {
      return (
        "La fecha de llegada no puede ser anterior a la salida (" +
        moment(fechaMinima).format("DD/MM/YYYY") +
        ")."
      );
    }
    return "";
  };

  const guardarRecibido = async (e) => {
    e.preventDefault();

    if (guardandoRef.current) return;

    const errorFecha = validarFecha();
    if (errorFecha) {
      setError(errorFecha);
      return;
    }

    guardandoRef.current = true;
    setGuardando(true);
    setError("");

    let cerrarAlTerminar = false;

    try {
      const res = await fetch(
        UrlApi + "/viajes_abastecimiento/" + row.id_viaje,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            nro_remito_puntodeventa: puntoVenta,
            nro_remito_numero: numeroRemito,
            fecha_llegada: fechaLlegada,
            recibido: 1,
            estado: 233,
          }),
        }
      );

      const data = await res.json().catch(() => null);

      // Esta ruta responde 200 también cuando hay un error de base de datos.
      if (!res.ok || data?.message !== "OK") {
        setError("No se pudo finalizar el viaje. Intentá nuevamente.");
        return;
      }

      logEventos(
        "Guardar remito",
        "Viajes a Sucursales",
        "Se finalizó el viaje " + row.id_viaje,
        localStorage.getItem("user")
      );

      cerrarAlTerminar = true;
    } catch (err) {
      console.error(err);
      setError("No se pudo conectar con el servidor. Intentá nuevamente.");
    } finally {
      guardandoRef.current = false;
      setGuardando(false);
    }

    if (cerrarAlTerminar && onClose) onClose();
  };

  return (
    <Dialog
      open={abrirDialogRecibido}
      onClose={cerrar}
      fullWidth
      maxWidth="xs"
    >
      <form onSubmit={guardarRecibido} autoComplete="off">
        <DialogTitle sx={{ pb: 1 }}>
          <Typography component="div" sx={{ fontSize: 17, fontWeight: 700 }}>
            Finalizar viaje N° {row?.id_viaje}
          </Typography>
          <Typography
            component="div"
            sx={{ fontSize: 12, color: "text.secondary" }}
          >
            Cargá la llegada y el remito para confirmar la recepción.
          </Typography>
        </DialogTitle>

        <DialogContent dividers>
          <Box
            sx={{
              bgcolor: "#E0F2F1",
              borderRadius: 1,
              p: 1.25,
              mb: 2,
            }}
          >
            <Grid container spacing={1}>
              <DatoViaje titulo="Producto" valor={row?.tipo_viaje} />
              <DatoViaje
                titulo="Proveedor"
                valor={
                  row?.prov_boca
                    ? row.prov_boca + " - " + row.nombre_boca
                    : ""
                }
              />
              <DatoViaje titulo="Destino" valor={row?.nombre_sucursal} />
              <DatoViaje titulo="Vehículo" valor={row?.patente} />
            </Grid>
          </Box>

          <Grid container spacing={1.5}>
            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                size="small"
                type="date"
                label="Fecha de llegada"
                value={fechaLlegada}
                onChange={(e) => {
                  setFechaLlegada(e.target.value);
                  setError("");
                }}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: fechaMinima, max: hoy }}
              />
            </Grid>
            <Grid item xs={5}>
              <TextField
                autoFocus
                required
                fullWidth
                size="small"
                label="Punto de venta"
                placeholder="0000"
                value={puntoVenta}
                onChange={(e) =>
                  setPuntoVenta(e.target.value.replace(/\D/g, ""))
                }
                inputProps={{ maxLength: 5, inputMode: "numeric" }}
              />
            </Grid>
            <Grid item xs={7}>
              <TextField
                required
                fullWidth
                size="small"
                label="Número de remito"
                placeholder="00000123"
                value={numeroRemito}
                onChange={(e) =>
                  setNumeroRemito(e.target.value.replace(/\D/g, ""))
                }
                inputProps={{ maxLength: 8, inputMode: "numeric" }}
              />
            </Grid>
          </Grid>

          {error ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          ) : null}
        </DialogContent>

        <DialogActions>
          <Button onClick={cerrar} disabled={guardando}>
            Volver
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="success"
            disabled={guardando}
          >
            {guardando ? "Finalizando..." : "Finalizar viaje"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

export default Dialog_recibido;